/**
 * StudioScheduler typed API client.
 *
 * Backed by `schema.d.ts` (auto-generated from FastAPI's OpenAPI spec by
 * `yarn generate-api`). Provides three tools:
 *
 *   1. `api(method, path, opts)` — typed fetch wrapper. `path` autocompletes
 *      against every backend route; `opts.body` is type-checked against the
 *      handler's declared body schema; the resolved Promise is typed against
 *      the response model.
 *
 *   2. `setAuthToken(token | null)` — set/clear the bearer token used for
 *      every subsequent request. Wired up by AuthContext on login/logout.
 *
 *   3. `ApiError` — thrown for any non-2xx response with `status` and the
 *      parsed error body so callers can branch on `e.status === 403` etc.
 *
 * Compile-time safety:
 *
 *   import { api } from '@/api';
 *   const r = await api('GET', '/api/studios');
 *   //          ^? Promise<StudioResponse[]>
 *   const fee = await api('GET', '/api/studio/fee-preview', {
 *     query: { amount: 20 },         // ← typo here is a TS error
 *   });
 *   //  ^? { gross: number, platform_fee: number, net_payout: number, ... }
 *
 * Runtime: plain fetch — no extra deps. Works in any JS/TS file in the app.
 */

import type { paths } from "./schema";

type Method = "get" | "post" | "put" | "delete" | "patch";
type UpperMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

type PathsWithMethod<M extends Method> = {
  [P in keyof paths]: M extends keyof paths[P] ? P : never;
}[keyof paths];

type OpResponse<P extends keyof paths, M extends Method> = paths[P] extends {
  [K in M]: { responses: infer R };
}
  ? R extends { 200: { content: { "application/json": infer T } } }
    ? T
    : R extends { 201: { content: { "application/json": infer T } } }
      ? T
      : void
  : void;

type OpRequestBody<P extends keyof paths, M extends Method> = paths[P] extends {
  [K in M]: { requestBody: { content: { "application/json": infer T } } };
}
  ? T
  : never;

type OpQuery<P extends keyof paths, M extends Method> = paths[P] extends {
  [K in M]: { parameters: { query?: infer Q } };
}
  ? Q
  : never;

type OpPathParams<P extends keyof paths, M extends Method> = paths[P] extends {
  [K in M]: { parameters: { path: infer Pp } };
}
  ? Pp
  : never;

export interface RequestOpts<P extends keyof paths, M extends Method> {
  body?: OpRequestBody<P, M>;
  query?: OpQuery<P, M>;
  /** Path-template interpolation, e.g. params: { booking_id: 'abc' }. */
  params?: OpPathParams<P, M>;
  /** Override or skip auth on a per-request basis. */
  authToken?: string | null;
  /** Pass-through extra fetch init (signal, headers etc). */
  init?: Omit<RequestInit, "body" | "method" | "headers"> & {
    headers?: Record<string, string>;
  };
}

export class ApiError<T = unknown> extends Error {
  status: number;
  body: T;
  constructor(status: number, body: T, message?: string) {
    super(message ?? `API error ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

// `process.env.REACT_APP_BACKEND_URL` is injected at build time by CRA. Declare
// a minimal shape so we don't need @types/node.
declare const process: { env: Record<string, string | undefined> } | undefined;

let _authToken: string | null = null;
let _baseUrl: string =
  (typeof process !== "undefined" && process.env.REACT_APP_BACKEND_URL) || "";

/** Set or clear the bearer token used by every subsequent request. */
export function setAuthToken(token: string | null): void {
  _authToken = token;
}

/** Override the base URL (mostly for tests). */
export function setBaseUrl(url: string): void {
  _baseUrl = url;
}

/** Iter-46af: Read the non-HttpOnly CSRF cookie set by /auth/login. The
 * value is mirrored back to the server in the ``X-CSRF-Token`` header on
 * every state-changing request so the double-submit pattern can match.
 */
function readCsrfCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)ss_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function interpolate(path: string, params: Record<string, unknown> | undefined): string {
  if (!params) return path;
  return path.replace(/\{(\w+)\}/g, (_, key) => {
    const v = params[key];
    if (v === undefined || v === null) {
      throw new Error(`Missing path param '${key}' for ${path}`);
    }
    return encodeURIComponent(String(v));
  });
}

function buildUrl(
  path: string,
  query: Record<string, unknown> | undefined,
  params: Record<string, unknown> | undefined,
): string {
  let url = `${_baseUrl}${interpolate(path, params)}`;
  if (!query) return url;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      for (const item of v) sp.append(k, String(item));
    } else {
      sp.append(k, String(v));
    }
  }
  const qs = sp.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function api<P extends keyof paths, M extends Method>(
  method: Uppercase<M> | UpperMethod,
  path: P & PathsWithMethod<M>,
  opts: RequestOpts<P, M> = {} as RequestOpts<P, M>,
): Promise<OpResponse<P, M>> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.init?.headers ?? {}),
  };
  const token = opts.authToken === undefined ? _authToken : opts.authToken;
  if (token) headers.Authorization = `Bearer ${token}`;

  // Iter-46af: attach CSRF header on state-changing requests when a cookie
  // is present (cookie set by /auth/login). For new cookie-only clients
  // this is the only auth secret; for legacy Bearer clients it's harmless.
  const upperMethod = String(method).toUpperCase();
  if (upperMethod === "POST" || upperMethod === "PUT" || upperMethod === "PATCH" || upperMethod === "DELETE") {
    const csrf = readCsrfCookie();
    if (csrf && !headers["X-CSRF-Token"]) headers["X-CSRF-Token"] = csrf;
  }

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (opts.body instanceof FormData) {
      body = opts.body;
    } else {
      headers["Content-Type"] = headers["Content-Type"] ?? "application/json";
      body = JSON.stringify(opts.body);
    }
  }

  const res = await fetch(
    buildUrl(
      path as string,
      opts.query as Record<string, unknown> | undefined,
      opts.params as Record<string, unknown> | undefined,
    ),
    {
      ...opts.init,
      method,
      headers,
      body,
      // Iter-46af: send/receive httpOnly auth cookies on every request.
      credentials: "include",
    },
  );

  const contentType = res.headers.get("content-type") ?? "";
  const parsed: unknown = contentType.includes("application/json")
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);

  if (!res.ok) {
    let message = `${method} ${path} → ${res.status}`;
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      const detail = (parsed as { detail: unknown }).detail;
      if (typeof detail === "string") message = detail;
    }
    throw new ApiError(res.status, parsed, message);
  }

  return parsed as OpResponse<P, M>;
}

/** Convenience aliases for the four common methods. */
export const apiGet = <P extends PathsWithMethod<"get">>(
  path: P,
  opts?: RequestOpts<P, "get">,
) => api<P, "get">("GET", path, opts);

export const apiPost = <P extends PathsWithMethod<"post">>(
  path: P,
  opts?: RequestOpts<P, "post">,
) => api<P, "post">("POST", path, opts);

export const apiPut = <P extends PathsWithMethod<"put">>(
  path: P,
  opts?: RequestOpts<P, "put">,
) => api<P, "put">("PUT", path, opts);

export const apiPatch = <P extends PathsWithMethod<"patch">>(
  path: P,
  opts?: RequestOpts<P, "patch">,
) => api<P, "patch">("PATCH", path, opts);

export const apiDelete = <P extends PathsWithMethod<"delete">>(
  path: P,
  opts?: RequestOpts<P, "delete">,
) => api<P, "delete">("DELETE", path, opts);

/** Re-export the generated schema namespace for direct access to schemas. */
export type { paths, components, operations } from "./schema";
